import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { Calculator, TrendingUp, Euro, Clock, Hammer, Paintbrush, Home, Bath, UtensilsCrossed, Building2, Wrench, RotateCcw, ArrowRight, CheckCircle, AlertTriangle, Info } from 'lucide-react';
import { formatCurrency } from '../lib/utils';

// Tipos de trabajo con datos de referencia realistas
const TIPOS_TRABAJO = [
  { 
    id: 'reforma_integral', 
    nombre: 'Reforma Integral', 
    icon: Home,
    descripcion: 'Reforma completa de vivienda',
    precioMin: 500, precioMax: 900, precioMedio: 700,
    horasEstimadas: { min: 200, max: 400 },
    duracionDias: { min: 45, max: 90 },
    unidad: '€/m²'
  },
  { 
    id: 'bano', 
    nombre: 'Baño Completo', 
    icon: Bath,
    descripcion: 'Sanitarios, alicatado, fontanería',
    precioMin: 800, precioMax: 1500, precioMedio: 1100,
    horasEstimadas: { min: 60, max: 120 },
    duracionDias: { min: 10, max: 20 },
    unidad: '€/m²'
  },
  { 
    id: 'cocina', 
    nombre: 'Cocina', 
    icon: UtensilsCrossed,
    descripcion: 'Mobiliario, encimera, instalaciones',
    precioMin: 600, precioMax: 1200, precioMedio: 850,
    horasEstimadas: { min: 80, max: 160 },
    duracionDias: { min: 15, max: 30 },
    unidad: '€/m²'
  },
  { 
    id: 'pintura', 
    nombre: 'Pintura', 
    icon: Paintbrush,
    descripcion: 'Pintura interior paredes y techos',
    precioMin: 12, precioMax: 25, precioMedio: 18,
    horasEstimadas: { min: 20, max: 60 },
    duracionDias: { min: 3, max: 10 },
    unidad: '€/m²'
  },
  { 
    id: 'fachada', 
    nombre: 'Fachada', 
    icon: Building2,
    descripcion: 'Rehabilitación, impermeabilización',
    precioMin: 35, precioMax: 60, precioMedio: 45,
    horasEstimadas: { min: 40, max: 120 },
    duracionDias: { min: 7, max: 21 },
    unidad: '€/m²'
  },
  { 
    id: 'reparaciones', 
    nombre: 'Reparaciones', 
    icon: Wrench,
    descripcion: 'Trabajos varios de mantenimiento',
    precioMin: 30, precioMax: 60, precioMedio: 45,
    horasEstimadas: { min: 4, max: 40 },
    duracionDias: { min: 1, max: 5 },
    unidad: '€/hora'
  },
];

const CalculadoraROI = () => {
  const [tipoTrabajo, setTipoTrabajo] = useState('reforma_integral');
  const [metros, setMetros] = useState('');
  const [costeMateriales, setCosteMateriales] = useState('');
  const [horasManoObra, setHorasManoObra] = useState('');
  const [costeHora, setCosteHora] = useState('25');
  const [otrosGastos, setOtrosGastos] = useState('');
  const [precioCliente, setPrecioCliente] = useState('');
  const [margenDeseado, setMargenDeseado] = useState('35');
  const [duracionDias, setDuracionDias] = useState('');
  const [resultado, setResultado] = useState(null);

  const tipoActual = TIPOS_TRABAJO.find(t => t.id === tipoTrabajo);

  const calcular = () => {
    const m2 = parseFloat(metros) || 0;
    const materiales = parseFloat(costeMateriales) || 0;
    const horas = parseFloat(horasManoObra) || 0;
    const precioHora = parseFloat(costeHora) || 25;
    const otros = parseFloat(otrosGastos) || 0;
    const precio = parseFloat(precioCliente) || 0;
    const margen = parseFloat(margenDeseado) || 35;
    const dias = parseFloat(duracionDias) || 0;

    // Calcular costes
    const costeManoObra = horas * precioHora;
    const costeTotal = materiales + costeManoObra + otros;

    // Calcular precio sugerido o usar el proporcionado
    let precioFinal = precio;
    if (precio <= 0 && costeTotal > 0) {
      precioFinal = costeTotal / (1 - margen / 100);
    }

    // Métricas
    const beneficioBruto = precioFinal - costeTotal;
    const margenReal = precioFinal > 0 ? (beneficioBruto / precioFinal) * 100 : 0;
    const roi = costeTotal > 0 ? (beneficioBruto / costeTotal) * 100 : 0;
    const precioM2 = m2 > 0 ? precioFinal / m2 : 0;
    const costeM2 = m2 > 0 ? costeTotal / m2 : 0;
    const beneficioPorHora = horas > 0 ? beneficioBruto / horas : 0;
    const beneficioPorDia = dias > 0 ? beneficioBruto / dias : 0;

    // Comparación con mercado
    let posicionMercado = 'sin_datos';
    if (tipoActual && m2 > 0 && tipoActual.precioMedio > 0) {
      if (precioM2 < tipoActual.precioMin) posicionMercado = 'bajo';
      else if (precioM2 > tipoActual.precioMax) posicionMercado = 'alto';
      else posicionMercado = 'competitivo';
    }

    // Recomendaciones
    const recomendaciones = [];
    
    if (margenReal < 20) {
      recomendaciones.push({ tipo: 'warning', texto: `Margen bajo (${margenReal.toFixed(1)}%). Considera subir el precio o reducir costes.` });
    } else if (margenReal >= 30 && margenReal <= 50) {
      recomendaciones.push({ tipo: 'success', texto: `Margen saludable (${margenReal.toFixed(1)}%). Buen equilibrio.` });
    } else if (margenReal > 50) {
      recomendaciones.push({ tipo: 'info', texto: `Margen alto (${margenReal.toFixed(1)}%). Podrías ser más competitivo.` });
    }

    if (beneficioPorHora > 0 && beneficioPorHora < 15) {
      recomendaciones.push({ tipo: 'warning', texto: `Beneficio por hora bajo (${beneficioPorHora.toFixed(0)}€). Revisa eficiencia.` });
    } else if (beneficioPorHora >= 30) {
      recomendaciones.push({ tipo: 'success', texto: `Excelente beneficio por hora (${beneficioPorHora.toFixed(0)}€).` });
    }

    if (posicionMercado === 'bajo') {
      recomendaciones.push({ tipo: 'info', texto: 'Precio por debajo del mercado. Podrías aumentar.' });
    } else if (posicionMercado === 'alto') {
      recomendaciones.push({ tipo: 'warning', texto: 'Precio por encima del mercado. Asegura valor añadido.' });
    }

    setResultado({
      costeTotal,
      costeMateriales: materiales,
      costeManoObra,
      otrosGastos: otros,
      precioSugerido: precioFinal,
      beneficioBruto,
      margenReal,
      roi,
      precioM2,
      costeM2,
      beneficioPorHora,
      beneficioPorDia,
      horas,
      dias,
      posicionMercado,
      recomendaciones
    });
  };

  const limpiar = () => {
    setMetros('');
    setCosteMateriales('');
    setHorasManoObra('');
    setCosteHora('25');
    setOtrosGastos('');
    setPrecioCliente('');
    setMargenDeseado('35');
    setDuracionDias('');
    setResultado(null);
  };

  const getRecomendacionStyle = (tipo) => {
    switch (tipo) {
      case 'success': return { bg: 'bg-green-50', border: 'border-green-200', icon: CheckCircle, iconColor: 'text-green-600' };
      case 'warning': return { bg: 'bg-amber-50', border: 'border-amber-200', icon: AlertTriangle, iconColor: 'text-amber-600' };
      default: return { bg: 'bg-blue-50', border: 'border-blue-200', icon: Info, iconColor: 'text-blue-600' };
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="calculadora-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Calculadora ROI</h1>
        <p className="text-slate-500 text-sm mt-1">Calcula la rentabilidad de tus trabajos de reforma</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Formulario */}
        <Card className="border-slate-200">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg flex items-center gap-2">
              <Calculator className="w-5 h-5" />
              Datos del Trabajo
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Tipo de trabajo con iconos */}
            <div className="space-y-2">
              <Label>Tipo de Trabajo</Label>
              <div className="grid grid-cols-3 gap-2">
                {TIPOS_TRABAJO.map((tipo) => {
                  const Icon = tipo.icon;
                  const isSelected = tipoTrabajo === tipo.id;
                  return (
                    <button
                      key={tipo.id}
                      type="button"
                      onClick={() => setTipoTrabajo(tipo.id)}
                      data-testid={`tipo-${tipo.id}`}
                      className={`p-3 rounded-lg border-2 transition-all text-center ${
                        isSelected 
                          ? 'border-slate-900 bg-slate-900 text-white' 
                          : 'border-slate-200 hover:border-slate-300 bg-white'
                      }`}
                    >
                      <Icon className={`w-5 h-5 mx-auto mb-1 ${isSelected ? 'text-white' : 'text-slate-500'}`} />
                      <span className="text-xs font-medium block">{tipo.nombre}</span>
                    </button>
                  );
                })}
              </div>
              {tipoActual && (
                <p className="text-xs text-slate-500 mt-2">
                  <span className="font-medium">Mercado:</span> {tipoActual.precioMin}-{tipoActual.precioMax} {tipoActual.unidad} • 
                  <span className="ml-1">{tipoActual.descripcion}</span>
                </p>
              )}
            </div>

            {/* Costes */}
            <div className="p-4 bg-slate-50 rounded-lg space-y-4">
              <p className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <Euro className="w-4 h-4" />
                Costes Estimados
              </p>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Superficie (m²)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    placeholder="Ej: 25"
                    value={metros}
                    onChange={(e) => setMetros(e.target.value)}
                    data-testid="metros-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Materiales (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="Ej: 2500"
                    value={costeMateriales}
                    onChange={(e) => setCosteMateriales(e.target.value)}
                    data-testid="materiales-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Horas M.O.</Label>
                  <Input
                    type="number"
                    step="0.5"
                    placeholder="40"
                    value={horasManoObra}
                    onChange={(e) => setHorasManoObra(e.target.value)}
                    data-testid="horas-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">€/Hora</Label>
                  <Input
                    type="number"
                    step="0.5"
                    value={costeHora}
                    onChange={(e) => setCosteHora(e.target.value)}
                    data-testid="coste-hora-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Otros (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="0"
                    value={otrosGastos}
                    onChange={(e) => setOtrosGastos(e.target.value)}
                    data-testid="otros-input"
                  />
                </div>
              </div>
            </div>

            {/* Duración y Margen */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-xs flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  Duración (días)
                </Label>
                <Input
                  type="number"
                  placeholder={tipoActual ? `${tipoActual.duracionDias.min}-${tipoActual.duracionDias.max}` : ''}
                  value={duracionDias}
                  onChange={(e) => setDuracionDias(e.target.value)}
                  data-testid="duracion-input"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Margen Deseado (%)</Label>
                <Select value={margenDeseado} onValueChange={setMargenDeseado}>
                  <SelectTrigger data-testid="margen-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="20">20% - Ajustado</SelectItem>
                    <SelectItem value="25">25% - Normal</SelectItem>
                    <SelectItem value="30">30% - Bueno</SelectItem>
                    <SelectItem value="35">35% - Óptimo</SelectItem>
                    <SelectItem value="40">40% - Alto</SelectItem>
                    <SelectItem value="50">50% - Premium</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Precio cliente opcional */}
            <div className="space-y-1 pt-3 border-t border-slate-200">
              <Label className="text-xs text-slate-500">
                Precio al Cliente (€) <span className="italic">— si ya lo tienes definido</span>
              </Label>
              <Input
                type="number"
                step="0.01"
                placeholder="Dejar vacío para calcular automáticamente"
                value={precioCliente}
                onChange={(e) => setPrecioCliente(e.target.value)}
                data-testid="precio-input"
              />
            </div>

            {/* Botones */}
            <div className="flex gap-2 pt-2">
              <Button 
                onClick={calcular} 
                className="flex-1 bg-slate-900 hover:bg-slate-800"
                data-testid="calcular-btn"
              >
                <Calculator className="w-4 h-4 mr-2" />
                Calcular
              </Button>
              <Button variant="outline" onClick={limpiar} data-testid="limpiar-btn">
                <RotateCcw className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Resultados */}
        <div className="space-y-4">
          {!resultado ? (
            <Card className="border-slate-200 h-full flex items-center justify-center">
              <CardContent className="text-center py-16">
                <TrendingUp className="w-16 h-16 mx-auto mb-4 text-slate-200" />
                <p className="text-slate-500">Introduce los datos para ver el análisis de rentabilidad</p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* KPIs principales */}
              <div className="grid grid-cols-2 gap-3">
                <Card className="border-slate-200">
                  <CardContent className="p-4 text-center">
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Coste Total</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1 font-mono">
                      {formatCurrency(resultado.costeTotal)}
                    </p>
                  </CardContent>
                </Card>
                <Card className="border-slate-200">
                  <CardContent className="p-4 text-center">
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Precio Sugerido</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1 font-mono">
                      {formatCurrency(resultado.precioSugerido)}
                    </p>
                  </CardContent>
                </Card>
                <Card className="border-green-100 bg-green-50">
                  <CardContent className="p-4 text-center">
                    <p className="text-xs text-green-700 uppercase tracking-wide">Beneficio Neto</p>
                    <p className="text-2xl font-bold text-green-700 mt-1 font-mono">
                      {formatCurrency(resultado.beneficioBruto)}
                    </p>
                  </CardContent>
                </Card>
                <Card className={`${
                  resultado.roi >= 50 ? 'border-green-100 bg-green-50' : 
                  resultado.roi >= 25 ? 'border-amber-100 bg-amber-50' : 
                  'border-red-100 bg-red-50'
                }`}>
                  <CardContent className="p-4 text-center">
                    <p className={`text-xs uppercase tracking-wide ${
                      resultado.roi >= 50 ? 'text-green-700' : 
                      resultado.roi >= 25 ? 'text-amber-700' : 
                      'text-red-700'
                    }`}>ROI</p>
                    <p className={`text-2xl font-bold mt-1 ${
                      resultado.roi >= 50 ? 'text-green-700' : 
                      resultado.roi >= 25 ? 'text-amber-700' : 
                      'text-red-700'
                    }`}>
                      {resultado.roi.toFixed(1)}%
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Barra de margen */}
              <Card className="border-slate-200">
                <CardContent className="p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Margen de beneficio</span>
                    <span className="font-bold">{resultado.margenReal.toFixed(1)}%</span>
                  </div>
                  <Progress value={Math.min(resultado.margenReal, 100)} className="h-3" />
                  <div className="flex justify-between text-xs text-slate-400">
                    <span>0%</span>
                    <span className="text-slate-600">Objetivo: {margenDeseado}%</span>
                    <span>100%</span>
                  </div>
                </CardContent>
              </Card>

              {/* Desglose de costes */}
              <Card className="border-slate-200">
                <CardContent className="p-4">
                  <p className="text-sm font-semibold text-slate-700 mb-3">Desglose de Costes</p>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Materiales</span>
                      <span className="font-mono">{formatCurrency(resultado.costeMateriales)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Mano de obra ({resultado.horas}h)</span>
                      <span className="font-mono">{formatCurrency(resultado.costeManoObra)}</span>
                    </div>
                    {resultado.otrosGastos > 0 && (
                      <div className="flex justify-between">
                        <span className="text-slate-600">Otros gastos</span>
                        <span className="font-mono">{formatCurrency(resultado.otrosGastos)}</span>
                      </div>
                    )}
                    <div className="flex justify-between pt-2 border-t border-slate-200 font-medium">
                      <span>Total</span>
                      <span className="font-mono">{formatCurrency(resultado.costeTotal)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Métricas adicionales */}
              <div className="grid grid-cols-2 gap-3">
                {resultado.precioM2 > 0 && (
                  <Card className="border-slate-200">
                    <CardContent className="p-3 text-center">
                      <p className="text-xs text-slate-500">Precio/m²</p>
                      <p className="text-lg font-bold text-slate-900 font-mono">{formatCurrency(resultado.precioM2)}</p>
                      {resultado.posicionMercado !== 'sin_datos' && (
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          resultado.posicionMercado === 'competitivo' ? 'bg-green-100 text-green-700' :
                          resultado.posicionMercado === 'bajo' ? 'bg-blue-100 text-blue-700' :
                          'bg-amber-100 text-amber-700'
                        }`}>
                          {resultado.posicionMercado === 'competitivo' ? 'En mercado' :
                           resultado.posicionMercado === 'bajo' ? 'Por debajo' : 'Por encima'}
                        </span>
                      )}
                    </CardContent>
                  </Card>
                )}
                {resultado.beneficioPorHora > 0 && (
                  <Card className="border-slate-200">
                    <CardContent className="p-3 text-center">
                      <p className="text-xs text-slate-500">Beneficio/Hora</p>
                      <p className="text-lg font-bold text-slate-900 font-mono">{formatCurrency(resultado.beneficioPorHora)}</p>
                    </CardContent>
                  </Card>
                )}
                {resultado.beneficioPorDia > 0 && (
                  <Card className="border-slate-200">
                    <CardContent className="p-3 text-center">
                      <p className="text-xs text-slate-500">Beneficio/Día</p>
                      <p className="text-lg font-bold text-slate-900 font-mono">{formatCurrency(resultado.beneficioPorDia)}</p>
                    </CardContent>
                  </Card>
                )}
                {resultado.costeM2 > 0 && (
                  <Card className="border-slate-200">
                    <CardContent className="p-3 text-center">
                      <p className="text-xs text-slate-500">Coste/m²</p>
                      <p className="text-lg font-bold text-slate-900 font-mono">{formatCurrency(resultado.costeM2)}</p>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Recomendaciones */}
              {resultado.recomendaciones.length > 0 && (
                <Card className="border-slate-200">
                  <CardContent className="p-4 space-y-2">
                    <p className="text-sm font-semibold text-slate-700 mb-2">Recomendaciones</p>
                    {resultado.recomendaciones.map((rec, i) => {
                      const style = getRecomendacionStyle(rec.tipo);
                      const Icon = style.icon;
                      return (
                        <div key={i} className={`flex items-start gap-2 p-3 rounded-lg border ${style.bg} ${style.border}`}>
                          <Icon className={`w-4 h-4 mt-0.5 ${style.iconColor}`} />
                          <p className="text-sm text-slate-700">{rec.texto}</p>
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>
      </div>

      {/* Referencia de precios */}
      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2 text-slate-700">
            <Info className="w-4 h-4" />
            Precios de Referencia (Barcelona)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {TIPOS_TRABAJO.map((tipo) => {
              const Icon = tipo.icon;
              return (
                <div key={tipo.id} className="text-center p-3 bg-slate-50 rounded-lg">
                  <Icon className="w-5 h-5 mx-auto text-slate-400 mb-1" />
                  <p className="text-xs font-medium text-slate-700">{tipo.nombre}</p>
                  <p className="text-xs text-slate-500 mt-1">
                    {tipo.precioMin}-{tipo.precioMax} {tipo.unidad}
                  </p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CalculadoraROI;
